import React, { useState } from 'react';
import { MdInsertLink, MdOutlineSearch } from 'react-icons/md';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { useUsersSearch } from 'legacy/search/hooks';
import UserList from '../UserList';
import { useDebounce } from 'use-debounce';
import { UserLean } from 'legacy/user/types';
import EmptyUserSelectionList from '../EmptyUserSelectionList';

const UserSelectionModal = ({
  users,
  onClose,
  onSubmit
}: {
  users: UserLean[];
  onClose: () => void;
  onSubmit: (users: UserLean[]) => void;
}) => {
  const [selectedUsers, setSelectedUsers] = useState<UserLean[]>(users);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 200);

  const { data: searchedUsers, isLoading } = useUsersSearch(debouncedSearch);

  const handleSubmit = () => {
    if (search.length > 0) {
      setSearch('');
    } else {
      onSubmit(selectedUsers);
    }
  };

  const eligibleUsersCount =
    selectedUsers.length > 0 ? `• ${selectedUsers.length}` : '';
  const footerText =
    search.length > 0
      ? 'Save Selection'
      : `Add Eligible Users ${eligibleUsersCount}`;

  const handleSearch = ({ currentTarget }) => {
    setSearch(currentTarget.value);
  };

  const showEmptyState =
    !isLoading &&
    !debouncedSearch &&
    !searchedUsers?.length &&
    !selectedUsers?.length;

  const handleSelectUser = (user: UserLean) => {
    const newSelectedUsers = selectedUsers.includes(user)
      ? selectedUsers.filter((u) => u._id !== user._id)
      : selectedUsers.concat(user);
    setSelectedUsers(newSelectedUsers);
  };

  const handleClearAll = () => {
    setSelectedUsers([]);
  };

  return (
    <Modal
      visible
      autoHeight
      type={ModalType.SMALL}
      onClose={onClose}
      shouldStopModalClose={false}
      title="Select Bounce Users"
      icon={<MdInsertLink size={20} />}
      renderFooter={() => (
        <Button
          type={ButtonType.Primary}
          onClick={handleSubmit}
          loading={false}
        >
          {footerText}
        </Button>
      )}
    >
      <div className="px-4 pt-4 flex flex-col justify-between">
        <p className="mt-4 text-base font-normal">
          Select eligible Bounce users to claim this offer. Once added, they
          will be able to claim it via email or in their Unclaimed Offers tab.
        </p>
        <div className="relative flex-shrink-0 flex h-12 border-1 border-gray-300 rounded-full mt-6">
          <MdOutlineSearch
            className="absolute left-3 text-gray-400 self-center justify-center"
            size={24}
          />
          <input
            type="text"
            className="w-full h-full px-11 rounded-full text-base text-gray-600 placeholder:text-gray-400"
            placeholder="Search Bounce Users"
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div>
          {showEmptyState ? (
            <EmptyUserSelectionList />
          ) : (
            <div className="overflow-y-auto mt-4">
              <UserList
                isLoading={isLoading}
                users={searchedUsers || selectedUsers}
                onSelect={handleSelectUser}
                selectedUsers={selectedUsers}
                search={search}
                onClearAll={handleClearAll}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UserSelectionModal;
